import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { askGPT } from '../components/OpenAIService';
import { Lightbulb, Edit3, X, Loader2, ChevronRight } from 'lucide-react';

// Import des modules de recommandations
import RecomDisplay from './RecomDisplay';
import RecomAction from './RecomAction';

/**
 * Page principale des recommandations avec polices harmonisées
 * Intègre les modules Display et Actions
 */
const Recommandations = ({ userId }) => {
  const { competenceId } = useParams();
  const navigate = useNavigate();
  
  // États
  const [competence, setCompetence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userGoals, setUserGoals] = useState('');
  const [userPlan, setUserPlan] = useState('');
  const [isEditingGoal, setIsEditingGoal] = useState(false);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [tempGoal, setTempGoal] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [isInitialSetup, setIsInitialSetup] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Définition des styles de police
  const fontStyles = `
    /* Styles de police pour le titre principal */
    .page-title {
      font-size: 1.75rem;
      line-height: 2.25rem;
      font-weight: 700;
    }
    
    /* Style pour les titres de section */
    .section-title {
      font-size: 1.5rem;
      line-height: 2rem;
      font-weight: 600;
    }
    
    /* Style pour le texte principal */
    .main-text {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    
    /* Style pour le texte de la zone d'objectif */
    .objective-text {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
    
    /* Style pour la zone de saisie */
    textarea.input-text {
      font-size: 1.125rem !important;
    }
    
    /* Style pour les boutons */
    .button-text {
      font-size: 1rem;
    }
  `;
  
  // Fonction pour générer les recommandations
  const generatePlan = useCallback(async (goal) => {
    if (!goal) return;
    
    try {
      let prompt = `Tu es un coach, conseiller spécialisé en développement personnel, éducatif et professionnel.
                   Ton rôle est d'informer l'étudiant et l'aider à réaliser son projet. 
                   Adresse toi directement à l'étudiant. 

                   Format de réponse obligatoire avec balises [SECTION1], [SECTION2], [SECTION3] :

                   [SECTION1]
                   {Présente le sujet et le contexte à l'étudiant, si l'étudiant à posé une question répond à sa question, sans titre ni introduction. En 100 mots max}

                   [SECTION2]
                   {Propose une démarche pour réaliser cette action, ou cet objectif. Préciser les questions ou les orientations qui restent à définir par l'utilisateur. Si le sujet n'est pas suffisament explicite, pose une ou deux questions pour clarifier le sens et l'objectif de l'étudiant, sans titre ni introduction. en 100 mots max. }

                   [SECTION3] 
                   {Formule tes recommandations et propositions et invite l'étudiant à poursuivre dans l'espace inspiration, sans titre ni introduction, en 70 mots max}

                   Important :
                   - Utilise le markdown pour le formatage
                   - Mets les mots clés en **gras** ou *italique*
                   - Utilise $...$ pour les formules en ligne, $...$  pour les formules affichées

                   Voici l'objectif de l'étudiant : "${goal}"`;

      const plan = await askGPT(prompt);
      
      if (plan) {
        await updateDoc(doc(db, 'users', userId, 'competences', competenceId), {
          recommendation: plan,
          recommendationUpdatedAt: new Date().toISOString()
        });
        setUserPlan(plan);
      }
    } catch (error) {
      console.error('Erreur de génération:', error);
      setUserPlan('Erreur lors de la génération du plan.');
    }
  }, [userId, competenceId]);

  // Sauvegarder un objectif modifié
  const handleSaveGoal = async () => {
    if (!competence || !tempGoal.trim()) return;
  
    try {
      setIsGeneratingPlan(true);
      const docRef = doc(db, 'users', userId, 'competences', competenceId);
      
      await updateDoc(docRef, {
        title: tempGoal,
        updatedAt: serverTimestamp(),
        recommendation: null,
        recommendationUpdatedAt: null
      });
      
      setUserGoals(tempGoal);
      setIsEditingGoal(false);
      setCompetence(prev => ({
        ...prev,
        title: tempGoal,
        updatedAt: new Date().toISOString()
      }));
      
      await generatePlan(tempGoal);
      setShowResults(true);
      setIsInitialSetup(false);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
    } finally {
      setIsGeneratingPlan(false);
    }
  };
  
  // Régénérer les recommandations
  const handleRegenerate = async () => {
    if (!userGoals || isGeneratingPlan) return;
    
    setIsGeneratingPlan(true);
    setError(null);
    const previousPlan = userPlan;
    
    try {
      setUserPlan('Génération des recommandations en cours...');
      await generatePlan(userGoals);
    } catch (error) {
      console.error('Erreur lors de la régénération:', error);
      setError('Impossible de générer de nouvelles recommandations.');
      setUserPlan(previousPlan);
    } finally {
      setIsGeneratingPlan(false);
    }
  };
  
  // Réviser l'objectif via la conversation
  const handleReviseObjective = () => {
    navigate('/objectif-detail', {
      state: {
        objectif: userGoals,
        type: competence?.theme === 'Professionnel' ? 'project' : 
              competence?.theme === 'Éducatif' ? 'idea' : 'desire',
        competenceId: competenceId
      }
    });
  };
  
  // Supprimer l'objectif
  const handleDeleteCompetence = async () => {
    if (!competenceId || !userId) return;
    
    try {
      setIsDeleting(true);
      await deleteDoc(doc(db, 'users', userId, 'competences', competenceId));
      navigate('/competences', { 
        state: { notification: `L'objectif a été supprimé avec succès.` } 
      });
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError('Impossible de supprimer cet objectif.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Charger la compétence au démarrage
  useEffect(() => {
    const fetchCompetence = async () => {
      if (!competenceId || !userId) {
        setError('Identifiant de compétence ou utilisateur non défini');
        setLoading(false);
        return;
      }
  
      try {
        const docSnap = await getDoc(doc(db, 'users', userId, 'competences', competenceId));
        
        if (docSnap.exists()) {
          const competenceData = { ...docSnap.data(), id: competenceId };
          setCompetence(competenceData);
          setUserGoals(competenceData.title || '');
          setTempGoal(competenceData.title || '');
          
          if (competenceData.recommendation) {
            setUserPlan(competenceData.recommendation);
            setShowResults(true);
            setIsInitialSetup(false);
          } else if (competenceData.title) {
            setShowResults(true);
            setIsInitialSetup(false);
            generatePlan(competenceData.title);
          }
        } else {
          setError('Aucune compétence trouvée.');
        }
      } catch (e) {
        console.error('Erreur de chargement:', e);
        setError('Erreur lors de la récupération de la compétence.');
      } finally {
        setLoading(false);
      }
    };
  
    setLoading(true);
    if (!auth.currentUser) {
      navigate('/');
    } else {
      fetchCompetence();
    }
  }, [competenceId, userId, navigate, generatePlan]);

  if (loading) return <div className="text-center p-8 main-text">Chargement...</div>;
  if (error) return <div className="text-red-600 p-8 main-text">{error}</div>;

  // Interface utilisateur principale
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Injecter les styles de police */}
      <style>{fontStyles}</style>
      
      {/* Fil d'Ariane avec liens actifs */}
      <div className="w-full bg-white border-b border-gray-200 px-6 py-3 mb-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center text-sm">
            <Link to="/Ambition" className="text-gray-600 hover:text-indigo-600 transition-colors">
              Ambition
            </Link>
            <ChevronRight className="h-4 w-4 mx-1 text-gray-400" />
            <span className="text-indigo-600 font-medium">Stratégie</span>
            <ChevronRight className="h-4 w-4 mx-1 text-gray-400" />
            <Link to={`/actionPlan/${competenceId}`} className="text-gray-600 hover:text-indigo-600 transition-colors">
              Plan d'actions
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 w-full">
        {/* En-tête avec l'objectif */}
        <div className="w-full bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl shadow-lg border border-gray-200 p-8 relative">
          {/* Boutons d'action en haut à droite */}
          <div className="absolute top-4 right-4 flex gap-2">
            <button
              onClick={() => isEditingGoal ? setIsEditingGoal(false) : setIsEditingGoal(true)}
              className="p-2 hover:bg-blue-100 rounded-full transition-colors duration-200"
              title={isEditingGoal ? "Annuler" : "Modifier"}
            >
              {isEditingGoal ? (
                <X className="h-5 w-5 text-gray-600" />
              ) : (
                <Edit3 className="h-5 w-5 text-blue-600" />
              )}
            </button>
            <button
              onClick={handleRegenerate}
              disabled={isGeneratingPlan}
              className={`p-2 rounded-full transition-colors duration-200 ${
                isGeneratingPlan ? 'bg-gray-100' : 'hover:bg-green-100'
              }`}
              title="Régénérer les suggestions"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${isGeneratingPlan ? 'text-gray-400 animate-spin' : 'text-green-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          <div className="flex justify-between items-center mb-6">
            <h2 className="page-title text-gray-800">
              {isInitialSetup ? "Partagez votre vision" : "Mon objectif"}
            </h2>
          </div>

          {isEditingGoal ? (
            <div className="space-y-6">
              <textarea
                value={tempGoal}
                onChange={(e) => setTempGoal(e.target.value)}
                className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent input-text bg-white shadow-inner"
                rows={4}
                placeholder="Décrivez votre objectif, vos aspirations, et ce que vous souhaitez accomplir..."
              />
              {isInitialSetup && (
                <div className="text-sm text-gray-500 mt-2">
                  Soyez précis et ambitieux
                </div>
              )}
              <div className="flex justify-end">
                <button
                  onClick={handleSaveGoal}
                  disabled={!tempGoal.trim() || isGeneratingPlan}
                  className={`px-8 py-3 rounded-lg flex items-center gap-3 button-text font-medium transition-all duration-200
                    ${tempGoal.trim() && !isGeneratingPlan
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md hover:shadow-lg'
                      : 'bg-gray-300 cursor-not-allowed text-gray-500'}`}
                >
                  {isGeneratingPlan ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    <span>{isInitialSetup ? "Découvrir mon potentiel" : "Sauvegarder"}</span>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="objective-text text-gray-700 p-4 bg-white bg-opacity-50 rounded-lg border border-gray-100">
              {userGoals}
            </div>
          )}
        </div>

        {/* Section des recommandations */}
        {showResults && (
          <div className="mt-12">
            <div className="w-full bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-lg border border-gray-200 p-8">
              <h2 className="section-title text-gray-800 mb-6 flex items-center">
                <Lightbulb className="h-6 w-6 mr-2 text-purple-600" />
                Recommandations
                {isGeneratingPlan && (
                  <div className="ml-3 flex items-center text-sm font-normal text-purple-600">
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    Mise à jour...
                  </div>
                )}
              </h2>

              {/* Module d'affichage des recommandations */}
              <RecomDisplay 
                content={userPlan}
              />
              
              {/* Module des actions sur les recommandations */}
              <RecomAction
                competenceId={competenceId}
                onReviseObjective={handleReviseObjective}
                onRegenerateRecommendations={handleRegenerate}
                onDeleteObjectif={handleDeleteCompetence}
                isGeneratingPlan={isGeneratingPlan}
                isDeleting={isDeleting}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Recommandations;